import { useState, useEffect } from "react";
import { toast } from "sonner";
import { OptionList, ChipRow } from "@/components/nutrition/NutritionParts";
import { optPts, beatMax, gradeChips } from "../beatUtils";
import { ConvShell, ClientBubble, TypingDots, UserBubble, TensionImpact } from "./Shared";
import { MessageSquare, ListChecks } from "lucide-react";

const TYPING_MS = 900;

export function ConvChoiceBeat({ beat, eyebrow, showBriefing, c, accent, onDone, onTensionDelta }) {
  const [idx, setIdx] = useState(null);
  const [res, setRes] = useState(null);
  const [typing, setTyping] = useState(true);
  useEffect(() => {
    setTyping(true);
    const t = setTimeout(() => setTyping(false), TYPING_MS);
    return () => clearTimeout(t);
  }, [beat.key]);
  const checked = !!res;
  const onPrimary = () => {
    if (!checked) {
      if (idx == null) return toast.error("Choose your reply.");
      const score = beat.options[idx].score;
      const delta = score >= 3 ? -18 : score === 1 ? -2 : 22;
      onTensionDelta?.(delta);
      setRes({ earned: optPts(score, beat.full ?? 20), max: beatMax(beat), detail: beat.detail || "", key: beat.key, label: beat.label, delta, replyText: beat.options[idx].t });
    } else onDone(res);
  };
  return (
    <ConvShell beat={beat} eyebrow={eyebrow} c={c} accent={accent} Icon={MessageSquare} checked={checked} onPrimary={onPrimary}>
      <div className="space-y-2 mb-4">
        {showBriefing && <ClientBubble>{c.briefing}</ClientBubble>}
        {typing ? <TypingDots /> : <ClientBubble>{beat.prompt}</ClientBubble>}
      </div>
      {checked && (
        <div className="mb-4">
          <UserBubble accent={accent}>{res.replyText}</UserBubble>
          <TensionImpact delta={res.delta} />
        </div>
      )}
      {!typing && (
        <>
          <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-1.5">Your reply</div>
          <OptionList options={beat.options} value={idx} onPick={setIdx} revealed={checked} testidPrefix={`${beat.key}-option`} />
        </>
      )}
    </ConvShell>
  );
}

export function ConvMultiBeat({ beat, eyebrow, showBriefing, c, accent, onDone, onTensionDelta }) {
  const [sel, setSel] = useState(new Set());
  const [res, setRes] = useState(null);
  const [typing, setTyping] = useState(true);
  useEffect(() => {
    setTyping(true);
    const t = setTimeout(() => setTyping(false), TYPING_MS);
    return () => clearTimeout(t);
  }, [beat.key]);
  const checked = !!res;
  const toggle = (i) => { const n = new Set(sel); n.has(i) ? n.delete(i) : n.add(i); setSel(n); };
  const onPrimary = () => {
    if (!checked) {
      const g = gradeChips(beat.chips, sel, beat.max ?? 20);
      const delta = Math.round((0.5 - g.pts / (beat.max ?? 20)) * 36);
      onTensionDelta?.(delta);
      setRes({ earned: g.pts, max: beatMax(beat), detail: `Caught ${g.caught}/${g.total}, ${g.falseTaps} false`, key: beat.key, label: beat.label, delta });
    } else onDone(res);
  };
  return (
    <ConvShell beat={beat} eyebrow={eyebrow} c={c} accent={accent} Icon={ListChecks} checked={checked} onPrimary={onPrimary}>
      <div className="space-y-2 mb-4">
        {showBriefing && <ClientBubble>{c.briefing}</ClientBubble>}
        {typing ? <TypingDots /> : <ClientBubble>{beat.prompt}</ClientBubble>}
      </div>
      {!typing && <ChipRow chips={beat.chips} selected={sel} onToggle={toggle} revealed={checked} testidPrefix={`${beat.key}-chip`} />}
      {checked && <TensionImpact delta={res.delta} />}
      {checked && (
        <div className="mt-2 space-y-1.5" data-testid={`${beat.key}-coaching`}>
          {beat.chips.filter((x) => x.flag).map((x, i) => (
            <div key={i} className="text-[11px] text-slate-600 leading-snug"><span className="text-emerald-600 font-semibold">⚑ {x.t}:</span> {x.coach}</div>
          ))}
        </div>
      )}
    </ConvShell>
  );
}
